import Grid from '@mui/material/Grid2';
import Typography from '@mui/material/Typography';
import { useContext, useEffect, useState } from 'react';
import { FormContainer, useForm } from 'react-hook-form-mui';
import ValidatedTextField from '../../Utilities/Forms/ValidatedTextField';
import { APIContext } from '../../Contexts/APIContext';
import { useLocation } from 'react-router-dom';
import DesignFeaturesViewModel from '../ViewModels/DesignFeaturesViewModel';
import DataContainer from '../../Utilities/DataContainer';
import EditControl from './EditControl';
import ConnectivityScoreBadge from '../ConnectivityScoreBadge';

interface IProps {
    isDftConnectivityToolEnabled: boolean;
}

export default function DesignFeatures({ isDftConnectivityToolEnabled }: IProps) {

    const { get, post, showMessage } = useContext(APIContext);
    const [readOnly, setReadOnly] = useState(true);
    const [parking, setParking] = useState<DesignFeaturesViewModel>(new DesignFeaturesViewModel());

    const location = useLocation();
    const params = new URLSearchParams(location.search);

    const siteRef = params.get('siteRef') || '';

    const formContext = useForm<DesignFeaturesViewModel>({
        defaultValues: new DesignFeaturesViewModel(),
    });

    const { reset } = formContext;

    useEffect(() => {
        if (siteRef) {
            getDesignFeaturesForSite();
            reset(new DesignFeaturesViewModel())
        }
    }, [siteRef,readOnly]);

    useEffect(() => {
        reset(parking);
    }, [parking]);

    const getDesignFeaturesForSite = () => {
        get('DesignFeatures', `GetDesignFeaturesForSite?reference=${siteRef}`, setParking)
    };

    const submit = (form: any) => {
        post('DesignFeatures', `UpdateDesignFeatures?reference=${siteRef}`, form, async () => {
            await getDesignFeaturesForSite()
            showMessage('success', 'Design features updated successfully')
            setReadOnly(true);
        });
    }

    return (
        <FormContainer formContext={formContext} onSuccess={submit}>
            <Grid container alignItems="flex-start">
                <Grid size={12}>
                    <Typography variant="h5">Design features encouraging non-car modes at the time of the survey</Typography>
                </Grid>
                <Grid size={6}>
                    <DataContainer title={"Pedestrians"} headerRight={isDftConnectivityToolEnabled &&
                        <ConnectivityScoreBadge label="Walking Connectivity Score" score={parking.walkingConnectivityScore} description="This measures how well the site connects to destinations on foot." />
                    }>
                        <Grid size={12}>
                            <Typography variant="body1">
                                For example: Footpath links, shared use paths, covered walkways, nearby pedestrian crossings, internal crossings of vehicle paths, all with specific attention to security.
                            </Typography>
                        </Grid>
                        <Grid size={12}>
                            <ValidatedTextField name={'pedestrians'} readOnly={readOnly} multiline={true} rows={5} />
                        </Grid>
                    </DataContainer>
                </Grid>
                <Grid size={6}>
                    <DataContainer title={"Pedal Cycles"} headerRight={isDftConnectivityToolEnabled &&
                        <ConnectivityScoreBadge label="Cycling Connectivity Score" score={parking.cyclingConnectivityScore} description="This measures how well the site connects to destinations by cycle." />
                    }>
                        <Grid size={12}>
                            <Typography variant="body1">
                                For example: On-site cycle paths, shared use paths, off-site cycle path connections, secure and other on-site/nearby cycle parking, shower facilities, cycle purchase schemes and other cycling incentives.
                            </Typography>
                        </Grid>
                        <Grid size={12}>
                            <ValidatedTextField name={'pedalCyclists'} readOnly={readOnly} multiline={true} rows={5} />
                        </Grid>
                    </DataContainer>
                </Grid>
                <Grid size={6}>
                    <DataContainer title={"Public Transport"} headerRight={isDftConnectivityToolEnabled &&
                        <ConnectivityScoreBadge label="Public Transport Connectivity Score" score={parking.publicTransportConnectivityScore} description="This measures how well the site connects to destinations by public transport." />
                    }>
                        <Grid size={12}>
                            <Typography variant="body1">
                                For example: Part of a bus/railway station development, on-site or nearby bus stop/railway station characteristics, nearby Park & Ride facility, on-site provision of service information, ticket deals for site users and other public transport incentives.
                            </Typography>
                        </Grid>
                        <Grid size={12}>
                            <ValidatedTextField name={'publicTransport'} readOnly={readOnly} multiline={true} rows={5} />
                        </Grid>
                    </DataContainer>
                </Grid>
            </Grid>
            <EditControl readOnly={readOnly} setReadOnly={setReadOnly} />
        </FormContainer >
    );
}
